import React, { useState, useEffect, useRef } from "react";
import { useForm, ValidationError } from "@formspree/react";
import CountriesDropdown from "../CountriesDropdown/CountriesDropdown";

import { useTranslation, Trans } from "react-i18next";

import "./MembershipApplication.css";

function MembershipApplication() {
  const [selectedCountries, setSelectedCountries] = useState([]);
  const { t } = useTranslation();
  const hasScrolledToError = useRef(false);

  const [state, handleSubmit] = useForm("xvgoozgg");

  // Scroll to first error when errors appear
  useEffect(() => {
    if (state.errors && state.errors.length > 0 && !hasScrolledToError.current) {
      hasScrolledToError.current = true;
      
      // Find the first error field
      const firstError = state.errors[0];
      const fieldName = firstError.field;
      
      // Map Formspree field names to actual input IDs
      const fieldIdMap = {
        'firstName': 'firstName',
        'lastName': 'lastName',
        'phone': 'phone',
        'email': 'email',
        'countryOfResidence': 'countryOfResidence',
        'cityOfResidence': 'cityOfResidence',
        'districtOfResidence': 'districtOfResidence',
        'countryOfBirth': 'countryOfBirth',
        'cityOfBirth': 'cityOfBirth',
        'occupation': 'occupation',
        'bio': 'bio',
        'socialLinks': 'socialLinks',
        'howDidYouHear': 'howDidYouHear',
        'referrer1Name': 'referrer1Name',
        'referrer1WhatsApp': 'referrer1WhatsApp',
        'referrer2Name': 'referrer2Name',
        'referrer2WhatsApp': 'referrer2WhatsApp',
        'communityCountries': 'communityCountries',
        'agreedToPolicy': 'agreedToPolicy'
      };
      
      const fieldId = fieldIdMap[fieldName] || fieldName;
      
      setTimeout(() => {
        // For checkbox, find the checkbox input
        if (fieldName === 'agreedToPolicy') {
          const checkbox = document.querySelector('input[name="agreedToPolicy"]');
          if (checkbox) {
            const checkboxContainer = checkbox.closest('.checkbox-container');
            if (checkboxContainer) {
              checkboxContainer.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
              setTimeout(() => {
                checkbox.focus();
              }, 300);
            }
          }
        } else {
          // For other fields, find the input/textarea/select
          const fieldElement = document.getElementById(fieldId);
          if (fieldElement) {
            fieldElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
            setTimeout(() => {
              fieldElement.focus();
            }, 300);
          }
        }
      }, 100);
    }
    
    // Reset scroll flag when form is submitted successfully or errors are cleared
    if (state.succeeded || (!state.errors || state.errors.length === 0)) {
      hasScrolledToError.current = false;
    }
  }, [state.errors, state.succeeded]);

  // Custom submit handler with scroll to first error for HTML5 validation
  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Check for HTML5 validation errors first
    const form = e.target;
    const firstInvalidField = form.querySelector(':invalid');
    if (firstInvalidField) {
      firstInvalidField.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
      setTimeout(() => {
        firstInvalidField.focus();
      }, 300);
      return;
    }
    
    // If no HTML5 validation errors, proceed with Formspree submission
    handleSubmit(e);
  };
  if (state.succeeded) {
    return (
      <section className="application-container">
        <p className="application-success-message">{t("applicationSuccessMessage")}</p>
      </section>
    );
  }

  return (
    <section className="application-container">
      <h1 className="application-header">{t("applicationHeader")}</h1>
      <p className="application-instructions">{t("applicationInstructions")}</p>
      <form
        onSubmit={handleFormSubmit}
        className="membership-form"
        noValidate
      >
        <label htmlFor="firstName">{t("firstNameLabel")} <span style={{ color: 'red' }}>*</span></label>
        <input
          id="firstName"
          type="text"
          name="firstName"
          required
          placeholder={t("firstNamePlaceholder")}
        />
        <ValidationError
          prefix="First Name"
          field="firstName"
          errors={state.errors}
        />

        <label htmlFor="lastName">{t("lastNameLabel")} <span style={{ color: 'red' }}>*</span></label>
        <input
          id="lastName"
          type="text"
          name="lastName"
          required
          placeholder={t("lastNamePlaceholder")}
        />
        <ValidationError
          prefix="Last Name"
          field="lastName"
          errors={state.errors}
        />

        <label htmlFor="phone">{t("phoneLabel")} <span style={{ color: 'red' }}>*</span></label>
        <input
          id="phone"
          type="tel"
          name="phone"
          required
          placeholder={t("phonePlaceholder")}
        />
        <ValidationError
          prefix="Phone"
          field="phone"
          errors={state.errors}
        />

        <label htmlFor="email">{t("emailLabel")} <span style={{ color: 'red' }}>*</span></label>
        <input
          id="email"
          type="email"
          name="email"
          required
          placeholder={t("emailPlaceholder")}
        />
        <ValidationError
          prefix="Email"
          field="email"
          errors={state.errors}
        />

        <label htmlFor="countryOfResidence">
          {t("countryOfResidenceLabel")} <span style={{ color: 'red' }}>*</span>
        </label>
        <input
          id="countryOfResidence"
          type="text"
          name="countryOfResidence"
          required
          placeholder={t("countryOfResidencePlaceholder")}
        />
        <ValidationError
          prefix="Country of Residence"
          field="countryOfResidence"
          errors={state.errors}
        />

        <label htmlFor="cityOfResidence">{t("cityOfResidenceLabel")} <span style={{ color: 'red' }}>*</span></label>
        <input
          id="cityOfResidence"
          type="text"
          name="cityOfResidence"
          required
          placeholder={t("cityOfResidencePlaceholder")}
        />
        <ValidationError
          prefix="City of Residence"
          field="cityOfResidence"
          errors={state.errors}
        />

        <label htmlFor="districtOfResidence">
          {t("districtOfResidenceLabel")} <span style={{ color: 'red' }}>*</span>
        </label>
        <input
          id="districtOfResidence"
          type="text"
          name="districtOfResidence"
          required
          placeholder={t("districtOfResidencePlaceholder")}
        />
        <ValidationError
          prefix="District of Residence"
          field="districtOfResidence"
          errors={state.errors}
        />

        <label htmlFor="countryOfBirth">{t("countryOfBirthLabel")} <span style={{ color: 'red' }}>*</span></label>
        <input
          id="countryOfBirth"
          type="text"
          name="countryOfBirth"
          required
          placeholder={t("countryOfBirthPlaceholder")}
        />
        <ValidationError
          prefix="Country of Birth"
          field="countryOfBirth"
          errors={state.errors}
        />

        <label htmlFor="cityOfBirth">{t("cityOfBirthLabel")} <span style={{ color: 'red' }}>*</span></label>
        <input
          id="cityOfBirth"
          type="text"
          name="cityOfBirth"
          required
          placeholder={t("cityOfBirthPlaceholder")}
        />
        <ValidationError
          prefix="City of Birth"
          field="cityOfBirth"
          errors={state.errors}
        />

        <label htmlFor="occupation">{t("occupationLabel")} <span style={{ color: 'red' }}>*</span></label>
        <input
          id="occupation"
          type="text"
          name="occupation"
          required
          placeholder={t("occupationPlaceholder")}
        />
        <ValidationError
          prefix="Occupation"
          field="occupation"
          errors={state.errors}
        />
        <div className="optional-fields-note">
          <Trans i18nKey="optionalFieldsNote" />
        </div>

        <label htmlFor="bio">{t("bioLabel")}</label>
        <textarea
          id="bio"
          name="bio"
          placeholder={t("bioPlaceholder")}
        />
        <ValidationError
          prefix="Bio"
          field="bio"
          errors={state.errors}
        />

        <label htmlFor="socialLinks">{t("socialLinksLabel")}</label>
        <textarea
          id="socialLinks"
          name="socialLinks"
          placeholder={t("socialLinksPlaceholder")}
        />
        <ValidationError
          prefix="Social Links"
          field="socialLinks"
          errors={state.errors}
        />

        <label htmlFor="howDidYouHear">{t("howDidYouHearLabel")}</label>
        <input
          id="howDidYouHear"
          type="text"
          name="howDidYouHear"
          placeholder={t("howDidYouHearPlaceholder")}
        />

        <label htmlFor="referrer1Name">{t("referrer1NameLabel")}</label>
        <input
          id="referrer1Name"
          type="text"
          name="referrer1Name"
          placeholder={t("referrer1NamePlaceholder")}
        />

        <label htmlFor="referrer1WhatsApp">{t("referrer1WhatsAppLabel")}</label>
        <input
          id="referrer1WhatsApp"
          type="tel"
          name="referrer1WhatsApp"
          placeholder={t("referrer1WhatsAppPlaceholder")}
        />

        <label htmlFor="referrer2Name">{t("referrer2NameLabel")}</label>
        <input
          id="referrer2Name"
          type="text"
          name="referrer2Name"
          placeholder={t("referrer2NamePlaceholder")}
        />

        <label htmlFor="referrer2WhatsApp">{t("referrer2WhatsAppLabel")}</label>
        <input
          id="referrer2WhatsApp"
          type="tel"
          name="referrer2WhatsApp"
          placeholder={t("referrer2WhatsAppPlaceholder")}
        />

        <label htmlFor="communityCountries">
          {t("communityCountriesLabel")}
        </label>
        <CountriesDropdown
          className="countries-dropdown"
          id="communityCountries"
          name="communityCountries"
          selectedCountries={selectedCountries}
          setSelectedCountries={setSelectedCountries}
          placeholder={t("select_countries_placeholder")}
          required
        />
        {/* Hidden input to submit selected countries */}
        <input
          type="hidden"
          name="communityCountries"
          value={selectedCountries.join(", ")}
        />
        <ValidationError
          prefix="Community Countries"
          field="communityCountries"
          errors={state.errors}
        />

        <label className="checkbox-container">
          <input
            type="checkbox"
            name="agreedToPolicy"
            required
          />
          {t("agreedToPolicyLabel")}
        </label>
        <ValidationError
          prefix="Agreement to Policy"
          field="agreedToPolicy"
          errors={state.errors}
        />

        <button
          type="submit"
          disabled={state.submitting}
        >
          {t("submitButton")}
        </button>
      </form>
    </section>
  );
}

export default MembershipApplication;
